import { zodResolver } from "@hookform/resolvers/zod";
import { Save, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { EnumSelect } from "@/components/shared/enum-select";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PRODUCT_STATUS_OPTIONS, PRODUCT_STATUSES, type ProductStatus } from "@/lib/enums";

const productFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(500, "Name is too long"),
  sku: z.string().min(1, "SKU is required").max(100, "SKU is too long"),
  price: z.coerce.number().min(0, "Price cannot be negative").max(1_000_000, "Price is too high"),
  description: z.string().max(2000, "Description is too long").optional(),
  status: z.enum(PRODUCT_STATUSES),
});

type ProductFormValues = z.infer<typeof productFormSchema>;

interface ProductFormResult {
  name: string;
  sku: string;
  priceCents: number;
  description: string | null;
  status: ProductStatus;
}

interface ProductFormDefaults {
  name?: string;
  sku?: string;
  priceCents?: number;
  description?: string | null;
  status?: ProductStatus;
}

interface ProductFormProps {
  defaultValues?: ProductFormDefaults;
  onSubmit: (result: ProductFormResult) => void;
  onCancel: () => void;
  isSaving: boolean;
  submitLabel?: string;
}

/** Reusable product form — drives both inline edit and the create dialog. */
export function ProductForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSaving,
  submitLabel = "Save",
}: ProductFormProps) {
  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      name: defaultValues?.name ?? "",
      sku: defaultValues?.sku ?? "",
      price: (defaultValues?.priceCents ?? 0) / 100,
      description: defaultValues?.description ?? "",
      status: defaultValues?.status ?? "draft",
    },
  });

  function handleSubmit(values: ProductFormValues) {
    onSubmit({
      name: values.name,
      sku: values.sku,
      priceCents: Math.round(values.price * 100),
      description: values.description?.trim() ? values.description : null,
      status: values.status,
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Product name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="sku"
            render={({ field }) => (
              <FormItem>
                <FormLabel>SKU</FormLabel>
                <FormControl>
                  <Input placeholder="SKU-001" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Price (USD)</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min="0" placeholder="0.00" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <FormControl>
                <EnumSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  options={PRODUCT_STATUS_OPTIONS}
                  className="w-[180px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea placeholder="Add a description..." className="min-h-[100px]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isSaving}>
            <Save className="h-4 w-4 mr-1" />
            {isSaving ? "Saving…" : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
